/**
 * Create Tenant (Super Admin)
 *
 * Creates a new tenant (organization) and its default workspace.
 * Does not require Clerk; uses current Supabase user from store.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { useUserStore } from '../../stores/prioritization/userStore';
import {
  createTenantAndDefaultWorkspace,
  type CreateTenantInput,
  type CreateTenantResult,
  type TenantPlan,
} from '../../lib/superAdminService';
const TENANT_KEY = 'peakPriorities_currentTenantId';
const WORKSPACE_KEY = 'peakPriorities_demoWorkspaceId';

export default function CreateTenant() {
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);

  const [formData, setFormData] = useState<CreateTenantInput>({
    name: '',
    slug: '',
    description: '',
    plan: 'starter',
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [createdIds, setCreatedIds] = useState<CreateTenantResult | null>(null);

  const handleNameChange = (name: string) => {
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 80);
    setFormData((prev) => ({ ...prev, name, slug }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) {
      setError('You must be logged in to create a tenant.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const result = await createTenantAndDefaultWorkspace(formData, userId);
      setCreatedIds(result);
      setSuccess(true);
      sessionStorage.setItem(TENANT_KEY, result.tenant_id);
      sessionStorage.setItem(WORKSPACE_KEY, result.workspace_id);
      setTimeout(() => navigate('/platform-admin/tenants'), 2500);
    } catch (err) {
      console.error('Error creating tenant:', err);
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
            ? String((err as { message?: unknown }).message)
            : 'Failed to create tenant';
      setError(message || 'Failed to create tenant');
    } finally {
      setCreating(false);
    }
  };

  if (success && createdIds) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="p-4 bg-[var(--green-dim)] rounded-full w-fit mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-[var(--green)]" />
          </div>
          <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-2">Tenant Created</h2>
          <p className="text-[var(--text-ter)] mb-4">
            &quot;{formData.name}&quot; and its default workspace are ready. You’ve been set as the tenant admin.
          </p>
          <p className="text-sm text-[var(--text-ter)]">Redirecting to tenants list…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <div className="mb-8">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <h1 className="text-2xl font-bold text-[var(--text-pri)]">Create Tenant</h1>
        <p className="text-[var(--text-ter)] mt-1">
          Create a new organization (tenant) and its default workspace. You will be the tenant admin.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Organization Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Acme Corporation"
            required
            className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Slug (URL identifier) *</label>
          <input
            type="text"
            value={formData.slug}
            onChange={(e) => setFormData((p) => ({ ...p, slug: e.target.value }))}
            placeholder="acme-corp"
            required
            pattern="[a-z0-9-]+"
            className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)]"
          />
          <p className="mt-1 text-sm text-[var(--text-ter)]">Lowercase letters, numbers, and hyphens only.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Description</label>
          <textarea
            value={formData.description || ''}
            onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
            placeholder="Brief description of this organization…"
            rows={3}
            className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)] resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Plan</label>
          <div className="grid grid-cols-4 gap-3">
            {(['free', 'starter', 'pro', 'enterprise'] as TenantPlan[]).map((plan) => (
              <button
                key={plan}
                type="button"
                onClick={() => setFormData((p) => ({ ...p, plan }))}
                className={`p-4 border  text-center transition-all ${
                  formData.plan === plan
                    ? 'border-[var(--coral)] bg-[var(--coral-dim)] ring-2 ring-red-200'
                    : 'border-[var(--rule)] hover:border-[var(--rule)]'
                }`}
              >
                <p className="font-medium text-[var(--text-pri)] capitalize">{plan}</p>
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-4 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)]">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            {error}
          </div>
        )}

        <div className="flex items-center gap-4 pt-4">
          <button
            type="submit"
            disabled={creating || !formData.name}
            className="flex-1 py-3 bg-[var(--coral)] text-white font-medium hover:bg-[var(--coral)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {creating ? (
              <>
                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Creating…
              </>
            ) : (
              <>
                <Building2 className="h-5 w-5" />
                Create Tenant
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-3 border border-[var(--rule)] text-[var(--text-sec)] font-medium hover:bg-[var(--surface)] transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
